import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntil, take, switchMap } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import {
  PlanPanelComponent,
  PlanPanel,
} from '../../shared/components/plan-panel/plan-panel.component';
import { PlanService } from '../../core/services/plan.service';
import { AuthService } from '../../core/services/auth.service';
import { Plan, GetPlansResponse } from '../../core/models/plan.model';
import { Icon } from '../../core/models/icon.model';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { UserFacade } from '../../core/store/user/user.facade';

interface PlanView extends Plan {
  pricePerPeriod: number;
  currency: string;
  productName: string;
  variationName: string;
  quantity: number | any;
  period: string;
  contractDate: string;
  contractDateRaw?: string;
  creationDate?: string;
  productIcon: string;
  productIconType: 'material-icons';
  uuid: string;
}

@Component({
  selector: 'umkt-plans',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    BreadcrumbComponent,
    LoadingComponent,
    CustomInputComponent,
    PlanPanelComponent,
    ErrorDisplayComponent,
    I18nPipe
  ],
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
})
export class PlansComponent implements OnInit, OnDestroy {
  plans: PlanView[] = [];
  planPanels: PlanPanel[] = [];
  isLoading = true;
  selectedFilter = 'all';
  selectedOrder = 'all';
  availableStatuses: string[] = [];
  isStatusSorted: boolean = false;
  originalPlans: PlanView[] = [];
  isContractSorted: boolean = false;
  isContractAsc: boolean = true;
  searchTerm: string = '';
  private destroy$ = new Subject<void>();

  // Propriedades de paginação
  currentPage: number = 0;
  itemsPerPage: number = 5;
  private baseItemsPerPageOptions: number[] = [5, 10, 15, 20];
  error: string | null = null;

  totalElements: number = 0;
  totalPagesFromServer: number = 0;
  currentUserId: string | null = null;
  isSelectorFocused: boolean = false;

  private _itemsPerPageOptions: number[] = [];
  private _itemsPerPageSelectOptions: { value: string; label: string }[] = [];
  private _lastTotalElements: number = -1;

  retryCount = 0;

  private langSubscription?: Subscription;

  breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Minha conta', url: '/my-account' },
    { label: 'Planos', url: '/my-account/plans' },
  ];

  filterOptions = [
    { value: 'all', label: 'pages.plans.filters.all' },
    { value: 'status-ativo', label: 'pages.plans.filters.active' },
    { value: 'status-inativo', label: 'pages.plans.filters.inactive' },
    {value:'status-suspenso', label: 'pages.plans.filters.suspended' }
  ];

  orderOptions = [
    { value: 'all', label: 'pages.plans.sort.all' },
    { value: 'price-asc', label: 'pages.plans.sort.priceAsc' },
    { value: 'price-desc', label: 'pages.plans.sort.priceDesc' },
    { value: 'creationDate-oldest', label: 'pages.plans.sort.oldest' },
    { value: 'creationDate-newest', label: 'pages.plans.sort.newest' },
  ];
  private planService = inject(PlanService);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private i18nService = inject(I18nService);
  private userFacade = inject(UserFacade);


  ngOnInit(): void {
    this.currentUserId = this.authService.getCurrentUserUuid();

    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      this.updateBreadcrumb();

      if (this.plans.length > 0) {
        this.updatePlanPanels();
      }
    });
    
    // Restaurar apenas a página atual dos query params (1-based na URL, 0-based interno)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['page'] !== undefined) {
        this.currentPage = +params['page'] - 1; // Converte de 1-based para 0-based
      }
      if (params['size'] !== undefined) {
        this.itemsPerPage = +params['size'];
      }
      if (params['status'] !== undefined) {
        this.selectedFilter = `status-${params['status']}`;
      }
      if (params['sort'] !== undefined) {
        this.selectedOrder = params['sort'];
      }
      if (params['search'] !== undefined) {
        this.searchTerm = params['search'];
      }
      
      this.updateBreadcrumb();
      
      if (this.currentUserId) {
        this.loadPlans(this.currentUserId);
      }
    });
  }

  private mapPlan(plan: Plan): PlanView {
    return {
      ...plan,
      status: plan.status || 'ativo',
      pricePerPeriod: plan.priceMonthly || plan.price,
      currency: 'BRL',
      productName: plan.productName,
      variationName: plan.variationName,
      quantity: plan.user?.role ? parseInt(plan.user.role) : plan.quantity,
      period: plan.billingCycle || 'período',
      contractDate: plan.creationDate ? this.formatDate(plan.creationDate) : '',
      contractDateRaw: plan.creationDate || '',
      productIcon: 'default',
      productIconType: 'material-icons',
    };
  }

  private getOrderParams(orderValue: string) {
    switch (orderValue) {
      case 'status-asc':
        return { sortField: 'status', sortDirection: 'asc' };
      case 'status-desc':
        return { sortField: 'status', sortDirection: 'desc' };
      case 'price-asc':
        return { sortField: 'priceMonthly', sortDirection: 'asc' };
      case 'price-desc':
        return { sortField: 'priceMonthly', sortDirection: 'desc' };
      case 'creationDate-oldest':
        return { sortField: 'creationDate', sortDirection: 'asc' };
      case 'creationDate-newest':
        return { sortField: 'creationDate', sortDirection: 'desc' };
      default:
        return { sortField: undefined, sortDirection: undefined };
    }
  }

  private getStatusForApi(filterValue: string): string | null {
    switch (filterValue) {
      case 'status-ativo':
        return 'ACTIVE';
      case 'status-inativo':
        return 'Inactive'.toUpperCase();
      case 'status-suspenso':
        return 'SUSPENDED';
      default:
        return null;
    }
  }

  handleRetry() {
    this.retryCount++;
    this.loadPlans();
  }

  loadPlans(userId?: string): void {
    const userUuid =
      userId || this.currentUserId || this.authService.getCurrentUserUuid();

    if (!userUuid) {
      this.error = this.i18nService.instant('pages.plans.errors.auth');
      this.router.navigate(['/login']);
      return;
    }

    this.isLoading = true;
    this.error = null;

    const orderParams = this.getOrderParams(this.selectedOrder);
    const apiStatus = this.getStatusForApi(this.selectedFilter);

    const finalParams = orderParams;

    const baseParams = {
      sortField: finalParams.sortField,
      sortDirection: finalParams.sortDirection,
      page: this.currentPage,
      size: this.itemsPerPage,
      status: apiStatus || undefined,
      searchTerm: this.searchTerm || undefined,
    };

    const request$ = this.authService.isFixaSession()
      ? this.userFacade.getSelectedContractDetails().pipe(
          take(1),
          switchMap((details) =>
            this.planService.getPlansByUserWithSort(userUuid, {
              ...baseParams,
              contractId: details?.customerAccountId,
            })
          )
        )
      : this.planService.getPlansByUserWithSort(userUuid, baseParams);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: GetPlansResponse) => {
          if (response && response.data && (response.data as any).content) {
            const data = response.data as any;
            this.plans = data.content.map((p: any) => this.mapPlan(p));
            this.originalPlans = [...this.plans];

            this.updatePlanPanels();

            this.totalElements = data.totalElements || data.content.length;
            this.totalPagesFromServer = data.totalPages || 1;
            this.clearCache();

            const statusesSet = new Set<string>();
            this.plans.forEach(plan => {
              if (plan.status && plan.status.trim() !== '') {
                statusesSet.add(plan.status.toLowerCase());
              }
            });
            this.availableStatuses = Array.from(statusesSet);
          } else {
            this.plans = [];
            this.originalPlans = [];
            this.planPanels = [];
            this.totalElements = 0;
            this.totalPagesFromServer = 0;
            this.clearCache();
          }
          this.retryCount = 0;
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('Erro ao carregar planos:', error);
          this.error = this.i18nService.instant('pages.plans.errors.load');
          this.isLoading = false;
          this.plans = [];
          this.originalPlans = [];
          this.planPanels = [];
          this.totalElements = 0;
          this.totalPagesFromServer = 0;
          this.clearCache();
        },
      });
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  onOrderChange(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  onSearch(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  private updateUrlParams(): void {
    const queryParams: any = {};
    

    if (this.currentPage > 0) {
      queryParams.page = this.currentPage + 1;
    }
    if (this.itemsPerPage !== 5) {
      queryParams.size = this.itemsPerPage;
    }
    if (this.selectedFilter && this.selectedFilter !== 'all') {
      // Remove o prefixo 'status-' para a URL ficar mais limpa
      queryParams.status = this.selectedFilter.replace('status-', '');
    }
    if (this.selectedOrder && this.selectedOrder !== 'all') {
      queryParams.sort = this.selectedOrder;
    }
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      queryParams.search = this.searchTerm.trim();
    }
    
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
    
    // O queryParams.subscribe() já vai chamar o load quando a URL mudar
  }

  private updateBreadcrumb(): void {
    const queryParams = this.route.snapshot.queryParams;
    const paginationParams: any = {};
    

    if (queryParams['page']) {
      paginationParams.page = queryParams['page'];
    }
    if (queryParams['size']) {
      paginationParams.size = queryParams['size'];
    }
    if (queryParams['status']) {
      paginationParams.status = queryParams['status'];
    }
    if (queryParams['sort']) {
      paginationParams.sort = queryParams['sort'];
    }
    if (queryParams['search']) {
      paginationParams.search = queryParams['search'];
    }
    

    if (!paginationParams.page && this.currentPage > 0) {
      paginationParams.page = this.currentPage + 1;
    }
    if (!paginationParams.size && this.itemsPerPage !== 5) {
      paginationParams.size = this.itemsPerPage;
    }
    if (!paginationParams.status && this.selectedFilter && this.selectedFilter !== 'all') {
      // Remove o prefixo 'status-' para a URL ficar mais limpa
      paginationParams.status = this.selectedFilter.replace('status-', '');
    }
    if (!paginationParams.sort && this.selectedOrder && this.selectedOrder !== 'all') {
      paginationParams.sort = this.selectedOrder;
    }
    if (!paginationParams.search && this.searchTerm && this.searchTerm.trim() !== '') {
      paginationParams.search = this.searchTerm.trim();
    }
      
    this.breadcrumbItems = [
      { label: this.i18nService.instant('shared.labels.myAccount'), url: '/my-account' },
      { 
        label: this.i18nService.instant('shared.labels.plans'), 
        url: '/my-account/plans',
        queryParams: Object.keys(paginationParams).length > 0 ? paginationParams : undefined
      },
    ];
  }

  toggleStatusSort(): void {
    if (!this.isStatusSorted) {
      this.plans = [...this.plans].sort((a, b) => {
        if (
          a.status.toLowerCase() === 'active' &&
          b.status.toLowerCase() !== 'active'
        )
          return -1;
        if (
          a.status.toLowerCase() !== 'active' &&
          b.status.toLowerCase() === 'active'
        )
          return 1;
        return 0;
      });
    } else {
      this.plans = [...this.originalPlans];
    }

    this.isStatusSorted = !this.isStatusSorted;
    this.updatePlanPanels();
  }

  toggleContractSort(): void {
    if (!this.isContractSorted) {
      this.plans = [...this.plans].sort((a, b) => {
        const dateA = new Date(a.contractDateRaw || '').getTime();
        const dateB = new Date(b.contractDateRaw || '').getTime();
        return dateB - dateA;
      });
      this.isContractSorted = true;
      this.isContractAsc = false;
    } else {
      if (!this.isContractAsc) {
        this.plans = [...this.plans].sort((a, b) => {
          const dateA = new Date(a.contractDateRaw || '').getTime();
          const dateB = new Date(b.contractDateRaw || '').getTime();
          return dateA - dateB;
        });
        this.isContractAsc = true;
      } else {
        this.plans = [...this.originalPlans];
        this.isContractSorted = false;
      }
    }
    this.updatePlanPanels();
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-active';
      case 'pending':
        return 'status-pending';
      case 'inactive':
        return 'status-inactive';
      case 'cancelled':
        return 'status-cancelled';
      case 'suspended':
        return 'status-suspended';
      default:
        return 'status-default';
    }
  }

  getStatusText(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Ativo';
      case 'pending':
        return 'Pendente';
      case 'inactive':
        return 'Inativo';
      case 'cancelled':
        return 'Cancelado';
      case 'alert':
        return 'Alerta';
      case 'suspended':
        return 'Suspenso';
      default:
        return 'Desconhecido';
    }
  }

  getStatusIcon(status: string): Icon {
    switch (status.toLowerCase()) {
      case 'active':
        return {
          name: 'check-circulo',
          type: 'mondrian-icons',
        };
      case 'pending':
        return {
          name: 'circulo-exclamacao',
          type: 'mondrian-icons',
        };
      case 'inactive':
        return {
          name: 'circulo-exclamacao',
          type: 'mondrian-icons',
        };
      case 'cancelled':
        return {
          name: 'fechar-circulo',
          type: 'mondrian-icons',
        };
      case 'alert':
        return {
          name: 'circulo-exclamacao',
          type: 'mondrian-icons',
        };
      case 'suspended':
        return {
          name: 'pause-vazio-circulo',
          type: 'mondrian-icons',
        };
      default:
        return {
          name: 'circulo-exclamacao',
          type: 'mondrian-icons',
        };
    }
  }


  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    } catch {
      return '';
    }
  }

  getQuantityText(quantity: number): string {
    return `${quantity} ${quantity === 1 ? 'licença' : 'licenças'}`;
  }

  getProductIcon(plan: PlanView): Icon {
    return {
      name: plan.productIcon,
      type: plan.productIconType,
      size: 'md',
    };
  }

  goToDetails(plan: PlanView): void {
    const planUuid = plan.uuid;
    if (planUuid) {
      this.planService.setSelectedPlanId(planUuid);
      const paginationParams: any = {};
      
      if (this.currentPage > 0) {
        paginationParams.page = this.currentPage + 1;
      }
      if (this.itemsPerPage !== 5) {
        paginationParams.size = this.itemsPerPage;
      }
      if (this.selectedFilter && this.selectedFilter !== 'all') {
        paginationParams.status = this.selectedFilter.replace('status-', '');
      }
      if (this.selectedOrder && this.selectedOrder !== 'all') {
        paginationParams.sort = this.selectedOrder;
      }
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        paginationParams.search = this.searchTerm.trim();
      }
      
      this.router.navigate(['/my-account/plans/details', planUuid], {
        state: { previousQueryParams: Object.keys(paginationParams).length > 0 ? paginationParams : undefined }
      });
    }
  }

  goBack(): void {
    window.history.back();
  }

  // Métodos de paginação
  onPageChange(page: number) {
    this.currentPage = page;
    this.updateUrlParams();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onItemsPerPageChange(newItemsPerPage: number) {
    const itemsPerPage = Number(newItemsPerPage);
    if (isNaN(itemsPerPage) || itemsPerPage <= 0) {
      console.error('Invalid itemsPerPage value:', newItemsPerPage);
      return;
    }

    this.itemsPerPage = itemsPerPage;
    this.currentPage = 0;
    this.updateUrlParams();
  }

  onItemsPerPageSelectorChange(newItemsPerPage: number) {
    this.onItemsPerPageChange(newItemsPerPage);
  }

  onSelectorFocus() {
    this.isSelectorFocused = true;
  }

  onSelectorBlur() {
    this.isSelectorFocused = false;
  }

  get itemsPerPageOptions(): number[] {
    if (
      this._lastTotalElements === this.totalElements &&
      this._itemsPerPageOptions.length > 0
    ) {
      return this._itemsPerPageOptions;
    }

    this._lastTotalElements = this.totalElements;
    const totalItems = this.totalItems;

    if (totalItems === 0) {
      this._itemsPerPageOptions = [5, 10, 15, 20];
      return this._itemsPerPageOptions;
    }

    const validOptions = this.baseItemsPerPageOptions.filter(
      option => option <= totalItems
    );

    const nextLargerOption = this.baseItemsPerPageOptions.find(
      option => option > totalItems
    );

    if (nextLargerOption && !validOptions.includes(nextLargerOption)) {
      validOptions.push(nextLargerOption);
    }

    if (validOptions.length === 0) {
      this._itemsPerPageOptions = [5, 10, 15, 20];
    } else {
      this._itemsPerPageOptions = validOptions.sort((a, b) => a - b);
    }

    if (!this._itemsPerPageOptions.includes(this.itemsPerPage)) {
      this.itemsPerPage = Math.max(...this._itemsPerPageOptions);
    }

    return this._itemsPerPageOptions;
  }

  get itemsPerPageSelectOptions(): { value: string; label: string }[] {
    const currentOptions = this.itemsPerPageOptions;
    const optionsHash = currentOptions.join(',');
    const currentHash = this._itemsPerPageSelectOptions
      .map(opt => opt.value)
      .join(',');

    if (optionsHash === currentHash) {
      return this._itemsPerPageSelectOptions;
    }

    this._itemsPerPageSelectOptions = currentOptions.map(option => ({
      value: option.toString(),
      label: option.toString(),
    }));

    return this._itemsPerPageSelectOptions;
  }

  get totalPages(): number {
    return this.totalPagesFromServer;
  }

  get totalItems(): number {
    return this.totalElements;
  }

  private clearCache() {
    this._itemsPerPageOptions = [];
    this._itemsPerPageSelectOptions = [];
    this._lastTotalElements = -1;
  }

  getPageInfoText(): string {
    if (this.totalElements === 0) {
      return 'pages.plans.paginationInfoEmpty';
    }
    return 'pages.plans.paginationInfo';
  }

  getPageInfoParams(): any {
    if (this.totalElements === 0) return {};

    const startItem = this.currentPage * this.itemsPerPage + 1;
    const endItem = Math.min(
      (this.currentPage + 1) * this.itemsPerPage,
      this.totalElements
    );

    return {
      start: startItem,
      end: endItem,
      total: this.totalElements
    };
  }

  getVisiblePages(): number[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: number[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 0; i < 5; i++) {
          visiblePages.push(i);
        }
        visiblePages.push(-1);
        visiblePages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 4) {
        visiblePages.push(0);
        visiblePages.push(-1);
        for (let i = totalPages - 5; i < totalPages; i++) {
          visiblePages.push(i);
        }
      } else {
        visiblePages.push(0);
        visiblePages.push(-1);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          visiblePages.push(i);
        }
        visiblePages.push(-1);
        visiblePages.push(totalPages - 1);
      }
    }

    return visiblePages;
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages && page !== this.currentPage) {
      this.onPageChange(page);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.goToPage(this.currentPage + 1);
    }
  }

  mapToPlanPanel(plan: PlanView): PlanPanel {
    return {
      uuid: plan.uuid,
      productName: plan.productName,
      variationName: plan.variationName,
      productUuid: plan.productUuid || undefined,
      parentProductUuid: plan.parentProduct?.uuid || undefined,
      quantity: plan.quantity,
      price: plan.price,
      priceMonthly: plan.priceMonthly || undefined,
      status: this.mapStatusToPanelStatus(plan.status),
      statusIcon: PlanPanelComponent.getStatusIcon(plan.status),
      contractDate: plan.contractDate,
      creationDate: plan.creationDate,
      expirationDate: plan.expirationDate,
      period: plan.period,
      showDetailsButton: true,
      contractPeriod: this.formatContractPeriod(plan.billingCycle),
      paymentMethod: this.formatPaymentMethod(plan),
      cardType: this.getCardType(plan),
      cardLastFourDigits: this.getCardLastFourDigits(plan),
    };
  }

  private mapStatusToPanelStatus(status: string): 'ativo' | 'inativo' | 'suspenso' {
    const normalized = status.toLowerCase().trim();
    switch (normalized) {
      case 'active':
      case 'ativo':
        return 'ativo';
      case 'inactive':
      case 'inativo':
        return 'inativo';
      case 'suspended':
      case 'suspenso':
        return 'suspenso';
      default:
        return 'inativo';
    }
  }

  private formatContractPeriod(billingCycle: string): string {
    if (!billingCycle) return '';

    switch (billingCycle.toLowerCase()) {
      case 'annual':
      case 'annually':
      case 'yearly':
        return this.i18nService.instant('pages.plans.termAnnual');
      case 'monthly':
      case 'month':
        return this.i18nService.instant('pages.plans.termMonthly');
      default:
        return billingCycle;
    }
  }

  private formatPaymentMethod(plan: any): string {
    return plan.cardFlag 
      ? this.i18nService.instant('pages.plans.methodCreditCard') 
      : this.i18nService.instant('pages.plans.methodBoleto');
  }

  private getCardType(plan: any): string | undefined {
    return plan.cardFlag || undefined;
  }

  private getCardLastFourDigits(plan: any): string | undefined {
    return plan.lastFourDigits || undefined;
  }

  onPlanDetails(planUuid: string): void {
    this.planService.setSelectedPlanId(planUuid);
    
    const paginationParams: any = {};
    
    if (this.currentPage > 0) {
      paginationParams.page = this.currentPage + 1;
    }
    if (this.itemsPerPage !== 5) {
      paginationParams.size = this.itemsPerPage;
    }
    if (this.selectedFilter && this.selectedFilter !== 'all') {
      paginationParams.status = this.selectedFilter.replace('status-', '');
    }
    if (this.selectedOrder && this.selectedOrder !== 'all') {
      paginationParams.sort = this.selectedOrder;
    }
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      paginationParams.search = this.searchTerm.trim();
    }
    
    this.router.navigate(['/my-account/plans/details', planUuid], {
      state: { previousQueryParams: Object.keys(paginationParams).length > 0 ? paginationParams : undefined }
    });
  }

  onManagePlan(planUuid: string): void {
    console.log('Gerenciar plano:', planUuid);
  }

  private updatePlanPanels(): void {
    this.planPanels = this.plans.map(plan => this.mapToPlanPanel(plan));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  formatItemsPerPageOption(option: number): string {
    return option.toString().padStart(2, '0');
  }
}
